import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, MessageCircle, ChevronRight, Link2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, waLink } from "@/lib/api";
import { TrackingForm } from "./TrackingForm";

const STAGES = ["Pedido recebido", "Pagamento confirmado", "Em cria\u00e7\u00e3o", "Em revis\u00e3o", "Entregue"];
const fmt = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR") : "");
const trackUrl = (code) => `${window.location.origin}/acompanhar/${code}`;

const StageControl = ({ t, onDone, i }) => {
  const [stage, setStage] = useState(t.stage);
  const [note, setNote] = useState("");
  const send = async () => {
    try {
      await http.post(`/admin/tracking/${t.id}/stage`, { stage: Number(stage), note });
      toast.success("Etapa atualizada \u2014 o cliente j\u00e1 v\u00ea em tempo real");
      setNote("");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-4 flex flex-col gap-2 border-t border-[#163D43] pt-4 sm:flex-row">
      <select value={stage} onChange={(e) => setStage(e.target.value)} className="h-9 rounded-md border border-[#163D43] bg-[#041214] px-3 text-sm text-teal-50" data-testid={`tracking-stage-select-${i}`}>
        {STAGES.map((s, k) => <option key={s} value={k}>{k + 1}. {s}</option>)}
      </select>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Recado para o cliente (opcional)" className="h-9 flex-1 rounded-md border border-[#163D43] bg-[#041214] px-3 text-sm text-teal-50" data-testid={`tracking-note-${i}`} />
      <button onClick={send} className="inline-flex h-9 items-center justify-center gap-1 rounded-full bg-[#2DD4BF] px-4 text-sm font-semibold text-[#041214]" data-testid={`tracking-stage-save-${i}`}>
        Atualizar <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

const Row = ({ t, i, onEdit, onReload }) => {
  const copy = () => {
    navigator.clipboard?.writeText(trackUrl(t.code));
    toast.success("Link copiado");
  };
  const remove = async () => {
    if (!window.confirm(`Excluir o pedido ${t.code}?`)) return;
    await http.delete(`/admin/tracking/${t.id}`).catch((e) => toast.error(errMsg(e)));
    onReload();
  };
  const msg = `Ol\u00e1, ${t.client_name.split(" ")[0]}! Seu pedido de ${t.service_title} foi registrado. C\u00f3digo: ${t.code}. Prazo de entrega: ${fmt(t.due_date)}. Acompanhe em tempo real: ${trackUrl(t.code)}`;
  return (
    <div className="rounded-md border border-[#163D43] bg-[#0B2326] p-4" data-testid={`tracking-row-${i}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-mono-x text-xs text-[#2DD4BF]">{t.code}</p>
          <p className="font-semibold text-teal-50">{t.service_title} — {t.client_name}</p>
          <p className="text-sm text-teal-300">Prazo: {fmt(t.due_date)} ({t.deadline_days} dias úteis) · <span className="text-teal-100">{STAGES[t.stage]}</span></p>
        </div>
        <div className="flex items-center gap-1">
          {t.client_phone && (
            <a href={waLink(t.client_phone, msg)} target="_blank" rel="noreferrer" className="rounded-full p-2 text-[#2DD4BF] hover:bg-[#163D43]" data-testid={`tracking-whatsapp-${i}`} aria-label="Enviar ao cliente"><MessageCircle className="h-4 w-4" /></a>
          )}
          <button onClick={copy} className="rounded-full p-2 text-teal-200 hover:bg-[#163D43]" data-testid={`tracking-copy-${i}`} aria-label="Copiar link"><Link2 className="h-4 w-4" /></button>
          <button onClick={() => onEdit(t)} className="rounded-full p-2 text-teal-200 hover:bg-[#163D43]" data-testid={`tracking-edit-${i}`} aria-label="Editar"><Pencil className="h-4 w-4" /></button>
          <button onClick={remove} className="rounded-full p-2 text-red-300 hover:bg-[#163D43]" data-testid={`tracking-delete-${i}`} aria-label="Excluir"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <StageControl key={`${t.id}-${t.stage}`} t={t} i={i} onDone={onReload} />
    </div>
  );
};

export const TrackingManager = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => http.get("/admin/tracking").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);
  return (
    <div data-testid="admin-tracking-manager">
      <button onClick={() => setEditing({})} data-testid="tracking-add" className="mb-6 inline-flex items-center gap-2 rounded-full bg-[#2DD4BF] px-5 py-2.5 text-sm font-semibold text-[#041214]">
        <Plus className="h-4 w-4" /> Novo pedido
      </button>
      <div className="grid gap-3">
        {items.map((t, i) => <Row key={t.id} t={t} i={i} onEdit={setEditing} onReload={load} />)}
        {!items.length && <p className="text-teal-200" data-testid="tracking-empty">Nenhum pedido ainda.</p>}
      </div>
      {editing && <TrackingForm item={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};
